import { useEffect, useReducer, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useHandleAppException, {
  isAppExceptionResponseData,
} from '../../../../hooks/useHandleAppException';
import { useProductColorsSelloutSummary } from '../../../../hooks/useProductColorsSelloutSummary';
import LangEnum from '../../../../lang/LangEnum';
import { TeceoApiResponse } from '../../../../lib/basicRepository';
import { getProductGridRepository } from '../../../../services/modules/product-grid/product-grid.repository';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import snackbarStore from '../../../../stores/Snackbar.store';
import { inputCodeWithoutZeroWidthSpace } from '../../../../utils/regexes';
import { OfflineStatus } from '../../../offline/domain/offlineOrder/offlineOrder.types';
import { AvailabilityEnum, ESProductColor } from '../../../products/ProductTypes';
import { useProductLock } from '../../../products/products-details-v2/hooks/useProductLock';
import { productActionsRepository } from '../../../products/repositories/productActions/productActions.repository';
import { productSearchRepository } from '../../../products/repositories/productSearch/productSearch.repository';
import {
  CodeMatchType,
  FindProductColorByAnyCodeResponse,
  ShopByOutcome,
  ShopByResultOutput,
} from '../../../products/repositories/types/productActions.types';
import { useTranslation } from '../../../translation/TranslationContext';
import { useOrderProductsContext } from '../../contexts/OrderProducts.context';
import { OrderStatus } from '../../interfaces/order-status.interface';
import { ordersRepository } from '../../repositories/ordersRepository/orders.repository';
import { formatOrderProductGridDTO } from '../helpers/orderProductGrid.formatter';
import { AddOrderProductMode } from '../orderProducts.types';
import { UpdateItemFormat } from '../orderProducts.types';
import { HandleSelectItemNewDeliveryWindow } from '../product/types/useOrderProductItem.types';
import {
  buildOrderItemsToCreate,
  findSearchedDeliveryWindowAvailability,
  getUpdateDeliveryWindowPayload,
} from './addOrderProduct.utils';
import {
  colorsFetchResult,
  initialState,
  onChangeProductInput,
  reducer,
  resetAddOrderProduct,
  resetColors,
  resetGridAndDeliveryWindow,
  resetOrderProductGrid,
  resetSelectedPacks,
  updateColor,
  updateDeliveryWindow,
  updateError,
  updateKit,
  updateLoadingColors,
  updateLoadingProduct,
  updateOrderItems,
  updateOrderProductGrid,
  updateOrderProductWithGrid,
  updateProduct,
  updateSelectedPacks,
  updateSuggestedGrid,
} from './addOrderProductForm.state';
import { useAddOrderProductGridActions } from './hooks/useAddOrderProductGridActions';
import useIncrementOrderItem from './hooks/useIncrementOrderItem';

interface UseAddOrderProductParams {
  orderId: string;
  orderStatus: OrderStatus | OfflineStatus;
  mode?: AddOrderProductMode;
}

export const useAddOrderProduct = ({ orderId, orderStatus, mode }: UseAddOrderProductParams) => {
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;
  const { handleErrorResponse } = useHandleAppException();
  const { pushOrderProducts, onSuccessAddProductWithPacks, isOfflineMode } =
    useOrderProductsContext();
  const { submitIncrement, submitAdd } = useIncrementOrderItem({ orderId });
  const lock = useProductLock();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolvingDeliveryWindow, setIsResolvingDeliveryWindow] = useState(false);
  const [isAutoSelectingSearchedProduct, setIsAutoSelectingSearchedProduct] = useState(false);
  const isSubmittingRef = useRef(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const searchingCodeRef = useRef<string | null>(null);
  const lastSearchedCodeRef = useRef<string | null>(null);
  const searchRequestIdRef = useRef(0);
  const [searchedProductColorId, setSearchedProductColorId] = useState<string | null>(null);
  const { selectedOrderCustomerId } = useOrderBookStore(
    useShallow((store) => ({ selectedOrderCustomerId: store.selectedOrder?.customer?.id })),
  );

  const productId = state.product?.id;
  const searchedColor = searchedProductColorId
    ? state.colors.find((item) => item.id === searchedProductColorId)
    : undefined;
  const color =
    state.color ?? searchedColor ?? (state.colors.length === 1 ? state.colors[0] : null);

  const isScanning = mode === AddOrderProductMode.SKU;
  const isProductMode = mode === AddOrderProductMode.PRODUCT;
  const canInsertByCode = !isOfflineMode;
  const shouldInsertOnSubmit = canInsertByCode && !isProductMode;

  const {
    showSuggestedGrid,
    suggestionAchieved,
    handleSelectSuggestion,
    handleSelectPacks,
    submitSelectedPacks,
  } = useAddOrderProductGridActions({
    orderId,
    orderStatus,
    availability: state.availability,
    productColorId: color?.id,
    deliveryWindow: state.deliveryWindow,
    grid: state.grid,
    createOrderItems: state.createOrderItems,
    selectedPacks: state.selectedPacks,
    suggestedGrid: state.suggestedGrid ?? [],
    onApplyQuantities: (updateItems) => dispatch(updateOrderProductWithGrid({ updateItems })),
    onSelectPacks: (packs) => dispatch(updateSelectedPacks({ packs })),
  });

  const { getSummary: getSelloutSummary } = useProductColorsSelloutSummary({
    orderId,
    productColorIds: color?.id ? [color.id] : [],
  });
  const selloutSummary = color?.id ? getSelloutSummary(color.id) : undefined;

  useEffect(() => {
    if (!productId) {
      dispatch(resetColors());
      return;
    }

    let isActive = true;
    dispatch(updateLoadingColors(true));

    productSearchRepository()
      .getProductSearchColors(productId, selectedOrderCustomerId)
      .then((response) => {
        if (isActive) {
          dispatch(colorsFetchResult(response));
        }
      })
      .catch(() => {
        if (isActive) {
          dispatch(updateLoadingColors(false));
          handleMessage({
            type: 'error',
            message: translate(LangEnum.SNACKBAR_ERROR_FETCHING_DATA),
          });
        }
      });

    return () => {
      isActive = false;
    };
  }, [productId, selectedOrderCustomerId]);

  useEffect(() => {
    let isActive = true;

    const fetchGrid = async () => {
      if (!color?.id || state.availability === AvailabilityEnum.EMPTY) {
        dispatch(resetOrderProductGrid());
        return;
      }

      const response = await getProductGridRepository().findProductGrid({
        productColorId: color.id,
        availability: state.availability,
        deliveryWindowId: state.deliveryWindow?.id,
        orderId,
      });

      if (!isActive) {
        return;
      }

      setIsAutoSelectingSearchedProduct(false);

      if (!response.ok || !response.data?.length) {
        const message = isAppExceptionResponseData(response.data)
          ? handleErrorResponse({ ok: false, data: response.data })
          : translate(LangEnum.SNACKBAR_ERROR_FETCHING_DATA);
        handleMessage({ type: 'error', message });
        dispatch(resetOrderProductGrid());
        return;
      }

      const orderProductGrid = formatOrderProductGridDTO({
        productAvailability: response.data[0],
        resetOrderItem: true,
      });

      const productAlreadyInOrder = orderProductGrid.some(
        (gridItem) => !!gridItem.orderItemId || !!gridItem.orderConditionId,
      );

      if (productAlreadyInOrder) {
        handleMessage({
          type: 'error',
          message: translate(LangEnum.API_EXCEPTION_ORDER_PRODUCT_ALREADY_IN_LIST),
        });
        dispatch(resetGridAndDeliveryWindow());
        return;
      }

      dispatch(
        updateOrderProductGrid({
          grid: orderProductGrid,
          selectedPacks: response.data[0].orderProductPack || [],
          suggestedPacks: response.data[0].suggestedPacks || [],
          hasValidPacks: response.data[0].hasValidPacks || false,
        }),
      );

      if (response.data[0]?.suggestedSkus) {
        dispatch(updateSuggestedGrid(response.data[0].suggestedSkus));
      }

      dispatch(
        updateOrderProductWithGrid({
          updateItems: orderProductGrid.map((gridItem) => ({
            skuId: gridItem.skuId,
            deliveryWindowId: state.deliveryWindow?.id,
            quantity: 0,
            availability: state.availability,
          })),
        }),
      );
    };

    fetchGrid();

    return () => {
      isActive = false;
    };
  }, [color?.id, state.availability, state.deliveryWindow, orderId]);

  const handleSelectColor = (selectedColor: ESProductColor) => {
    setIsAutoSelectingSearchedProduct(false);
    dispatch(updateColor(selectedColor));
    dispatch(resetOrderProductGrid());
  };

  const handleSelectDeliveryWindow: HandleSelectItemNewDeliveryWindow = ({ newDwAvailability }) => {
    setIsAutoSelectingSearchedProduct(false);
    dispatch(updateDeliveryWindow(getUpdateDeliveryWindowPayload(newDwAvailability)));
  };

  const handleChangeGridItem = (item: UpdateItemFormat) => {
    dispatch(updateOrderItems({ updateItem: item }));
  };

  const handleChangeCode = (value: string) => {
    dispatch(onChangeProductInput(value));
  };

  const searchProductByCode = (code: string) =>
    productActionsRepository().searchProduct({
      codeOrBarcodeOrName: code,
      orderId,
    });

  const applySearchedAvailability = async (
    searched: FindProductColorByAnyCodeResponse,
    requestId: number,
  ) => {
    if (!searched.availability || searched.availability === AvailabilityEnum.EMPTY) {
      return;
    }

    if (searched.availability === AvailabilityEnum.READY_FOR_DELIVERY) {
      dispatch(updateDeliveryWindow(getUpdateDeliveryWindowPayload(undefined)));
      return;
    }

    setIsResolvingDeliveryWindow(true);

    const response = await ordersRepository(orderId).getOrderProductDeliveryWindows(
      searched.productId,
      searched.productColorId,
    );

    if (searchRequestIdRef.current !== requestId) {
      return;
    }

    setIsResolvingDeliveryWindow(false);

    if (!response.ok || !response.data) {
      return;
    }

    const deliveryWindowAvailability = findSearchedDeliveryWindowAvailability({
      windows: response.data,
      availability: searched.availability,
      deliveryWindowId: searched.deliveryWindowId,
    });

    if (deliveryWindowAvailability) {
      dispatch(updateDeliveryWindow(getUpdateDeliveryWindowPayload(deliveryWindowAvailability)));
    }
  };

  const applyShopByResponse = (
    response: TeceoApiResponse<ShopByResultOutput>,
    requestId: number,
  ) => {
    const isCurrentRequest = searchRequestIdRef.current === requestId;

    if (!response.ok || !response.data) {
      const message = isAppExceptionResponseData(response.data)
        ? handleErrorResponse({ ok: false, data: response.data })
        : translate(LangEnum.ERROR_UPDATING_ITEM_MESSAGE);
      handleMessage({ type: 'error', message });

      if (isCurrentRequest) {
        dispatch(updateError({ hasError: true, errorMessage: message }));
      }

      return;
    }

    if (!isCurrentRequest) {
      return;
    }

    if (response.data.outcome === ShopByOutcome.ALREADY_IN_ORDER) {
      handleMessage({
        type: 'info',
        message: translate(LangEnum.ORDER_PRODUCT_ALREADY_IN_ORDER),
      });
    }

    dispatch(resetAddOrderProduct());
    codeInputRef.current?.focus();
  };

  const startCodeRequest = (code: string) => {
    dispatch(onChangeProductInput(code));

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;

    setSearchedProductColorId(null);
    dispatch(updateLoadingProduct(true));

    return requestId;
  };

  const insertByCode = async (code: string) => {
    searchingCodeRef.current = code;

    const requestId = startCodeRequest(code);

    if (isScanning) {
      dispatch(onChangeProductInput(''));
    }

    const response = isScanning ? await submitIncrement(code) : await submitAdd(code);

    if (searchingCodeRef.current === code) {
      searchingCodeRef.current = null;
    }

    applyShopByResponse(response, requestId);
  };

  const previewByCode = async (code: string) => {
    const isCodeAlreadyResolved =
      lastSearchedCodeRef.current === code &&
      (!!state.product?.id || !!state.kit || state.hasError);

    if (isCodeAlreadyResolved) {
      return;
    }

    searchingCodeRef.current = code;
    lastSearchedCodeRef.current = code;

    const requestId = startCodeRequest(code);

    setIsAutoSelectingSearchedProduct(true);

    const response = await searchProductByCode(code);

    if (searchingCodeRef.current === code) {
      searchingCodeRef.current = null;
    }

    if (searchRequestIdRef.current !== requestId) {
      return;
    }

    if (response.ok && response.data && 'kitId' in response.data) {
      const searchedKit = response.data;

      dispatch(
        updateKit({
          id: searchedKit.kitId,
          code: searchedKit.kitCode,
          name: searchedKit.kitName,
        }),
      );

      setIsAutoSelectingSearchedProduct(false);
      return;
    }

    if (response.ok && response.data && 'productId' in response.data) {
      const searched = response.data;

      dispatch(
        updateProduct({
          id: searched.productId,
          code: searched.productCode,
          name: searched.productName,
        }),
      );

      const identifiesColor = searched.matchType !== CodeMatchType.PRODUCT_CODE;

      if (!identifiesColor || isProductMode) {
        setIsAutoSelectingSearchedProduct(false);
        return;
      }

      setSearchedProductColorId(searched.productColorId);

      await applySearchedAvailability(searched, requestId);
      return;
    }

    const errorMessage = isAppExceptionResponseData(response.data)
      ? handleErrorResponse({ ok: false, data: response.data })
      : undefined;

    dispatch(updateError({ hasError: true, errorMessage }));
  };

  const handleSubmitCode = (rawCode: string) => {
    const code = inputCodeWithoutZeroWidthSpace(rawCode);

    if (!code || searchingCodeRef.current === code) {
      return;
    }

    return shouldInsertOnSubmit ? insertByCode(code) : previewByCode(code);
  };

  const handleBlurCode = (rawCode: string) => {
    const code = inputCodeWithoutZeroWidthSpace(rawCode);

    if (!code || searchingCodeRef.current === code) {
      return;
    }

    return previewByCode(code);
  };

  const changeSubmitting = (value: boolean) => {
    isSubmittingRef.current = value;
    setIsSubmitting(value);
  };

  const finishAddProduct = () => {
    dispatch(resetAddOrderProduct());
    lastSearchedCodeRef.current = null;
    changeSubmitting(false);
    codeInputRef.current?.focus();
  };

  const addProductWithSelectedPacks = async () => {
    const response = await submitSelectedPacks();

    if (!response.ok) {
      changeSubmitting(false);
      return;
    }

    await onSuccessAddProductWithPacks(state.availability, color?.id);
    finishAddProduct();
  };

  const addProductWithoutDeliveryWindow = async (productColorId: string) => {
    const response = await productActionsRepository().shopProduct({ productColorId, orderId });

    if (!response.ok) {
      const message = isAppExceptionResponseData(response.data)
        ? handleErrorResponse({ ok: false, data: response.data })
        : translate(LangEnum.ERROR_UPDATING_ITEM_MESSAGE);
      handleMessage({ type: 'error', message });
      changeSubmitting(false);
      return;
    }

    await onSuccessAddProductWithPacks(state.availability, productColorId);
    finishAddProduct();
  };

  const handleAddProduct = async () => {
    if (isSubmittingRef.current) {
      return;
    }

    if (state.kit) {
      changeSubmitting(true);
      await insertByCode(state.kit.code);
      changeSubmitting(false);
      return;
    }

    if (state.availability === AvailabilityEnum.EMPTY) {
      if (!canInsertByCode || !color) {
        handleMessage({
          type: 'warning',
          message: translate(LangEnum.ADD_ORDER_PRODUCTS_EMPTY_AVAILABILITY_WARNING_MESSAGE),
        });
        return;
      }

      changeSubmitting(true);
      await addProductWithoutDeliveryWindow(color.id);
      return;
    }

    if (state.selectedPacks.length > 0) {
      changeSubmitting(true);
      await addProductWithSelectedPacks();
      return;
    }

    const orderItemsToCreate = buildOrderItemsToCreate({
      createOrderItems: state.createOrderItems,
      grid: state.grid,
      availability: state.availability,
      deliveryWindowId: state.deliveryWindow?.id,
    });

    if (!orderItemsToCreate.length) {
      return;
    }

    changeSubmitting(true);

    const response = await ordersRepository(orderId).createOrderProduct(orderItemsToCreate);

    if (!response.ok) {
      const message = isAppExceptionResponseData(response.data)
        ? handleErrorResponse({ ok: false, data: response.data })
        : translate(LangEnum.ERROR_UPDATING_ITEM_MESSAGE);
      handleMessage({ type: 'error', message });
      changeSubmitting(false);
      return;
    }

    if (!response.data) {
      changeSubmitting(false);
      return;
    }

    pushOrderProducts(response.data);
    finishAddProduct();
  };

  const isGridPending =
    !!color && state.availability !== AvailabilityEnum.EMPTY && state.grid.length === 0;

  const isPreparingSearchedProduct =
    isAutoSelectingSearchedProduct &&
    (state.loading || state.loadingColors || isResolvingDeliveryWindow || isGridPending);

  const canAddWithoutDeliveryWindow =
    canInsertByCode &&
    !!color &&
    state.availability === AvailabilityEnum.EMPTY &&
    !isPreparingSearchedProduct;

  return {
    inputProductValue: state.inputProductValue,
    product: state.product,
    kit: state.kit,
    isLoadingProduct: state.loading,
    hasError: state.hasError,
    errorMessage: state.errorMessage,
    colors: state.colors,
    color,
    isLoadingColors: state.loadingColors,
    availability: state.availability,
    deliveryWindow: state.deliveryWindow,
    isResolvingDeliveryWindow,
    isPreparingSearchedProduct,
    canAddWithoutDeliveryWindow,
    grid: state.grid,
    createOrderItems: state.createOrderItems,
    totals: state.totals,
    orderId,
    orderStatus,
    isSubmitting,
    codeInputRef,
    suggestedGrid: state.suggestedGrid ?? [],
    suggestedPacks: state.suggestedPacks ?? [],
    selectedPacks: state.selectedPacks,
    hasValidPacks: state.hasValidPacks,
    showSuggestedGrid,
    suggestionAchieved,
    selloutSummary,
    getLimit: lock.getLimit,
    getStockValue: lock.getStockValue,
    handleChangeCode,
    handleSubmitCode,
    handleBlurCode,
    handleSelectColor,
    handleSelectDeliveryWindow,
    handleChangeGridItem,
    handleSelectSuggestion,
    handleSelectPacks,
    handleAddProduct,
    resetSelectedPacks: () => dispatch(resetSelectedPacks()),
  };
};
